import { useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSessionStorage, setSessionStorageItem } from './useSessionStorage';

const CURRENT_ROUTE_KEY = 'currentRoute';
const PREVIOUS_ROUTE_KEY = 'previousRoute';
const defaultRoute = '/home';

/**
 * Hook to track routes at the router level.
 * We need to track routes at the router level regardles of browser history
 * because of page reloads breaking our back button navigation in the mobile menu header
 */
export const useRouteTracking = () => {
  const location = useLocation();
  const lastPathRef = useRef(location.pathname + location.search);
  const currentRoute = useSessionStorage(CURRENT_ROUTE_KEY);
  const previousRoute = useSessionStorage(PREVIOUS_ROUTE_KEY);

  // This useEffect is necessary to keep track of the current and previous routes when the user reloads the page
  useEffect(() => {
    const path = location.pathname + location.search;
    if (path !== lastPathRef.current) {
      if (currentRoute && currentRoute !== path) {
        setSessionStorageItem(PREVIOUS_ROUTE_KEY, currentRoute);
      }

      setSessionStorageItem(CURRENT_ROUTE_KEY, path);
      lastPathRef.current = path;
    }
  }, [location, currentRoute, previousRoute]);
};

/**
 * Hook to handle back navigation independent of browser history
 * Updates session storage routes to track the new current and previous routes
 * @returns A function to navigate back
 */
export const useBackNavigation = () => {
  const history = useHistory();
  const location = useLocation();
  const previousRoute = useSessionStorage(PREVIOUS_ROUTE_KEY);
  const lastPathRef = useRef(location.pathname + location.search);

  const updateSessionStorageRoutes = (targetPath: string) => {
    const currentPath = location.pathname + location.search;
    setSessionStorageItem(CURRENT_ROUTE_KEY, targetPath);
    setSessionStorageItem(PREVIOUS_ROUTE_KEY, currentPath);
    lastPathRef.current = targetPath;
  };

  const navigateBack = () => {
    const targetRoute = previousRoute || defaultRoute;
    const currentPath = location.pathname + location.search;

    if (currentPath === targetRoute) {
      updateSessionStorageRoutes(defaultRoute);
      history.push(defaultRoute);
    } else {
      updateSessionStorageRoutes(targetRoute);
      history.push(targetRoute);
    }
  };

  return navigateBack;
};
